import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { financiamentosApi, categoriasApi, contasApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Input, Select } from '../../components/ui/Input'
import { Card, CardHeader, AvatarIcon, ProgressBar } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { LoadingState, EmptyState, ErrorState } from '../../components/ui/EmptyState'
import { Modal, ConfirmDialog } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import {
  SistemaAmortizacao, StatusFinanciamento, TipoRecorrencia, IndiceCorrecaoMonetaria,
  SISTEMA_AMORTIZACAO_LABEL, STATUS_FINANCIAMENTO_LABEL, TIPO_RECORRENCIA_LABEL, INDICE_CORRECAO_LABEL,
} from '../../lib/enums'
import { formatMoney, formatDate } from '../../lib/utils'
import type { FinanciamentoResumo, SimulacaoFinanciamento } from '../../types'

const PERIODICIDADES: TipoRecorrencia[] = [
  TipoRecorrencia.Mensal, TipoRecorrencia.Quinzenal, TipoRecorrencia.Bimestral, TipoRecorrencia.Anual,
]

interface FormState {
  accountId: string
  categoriaId: string
  descricao: string
  instituicao: string
  valorTotal: string
  valorEntrada: string
  dataEntrada: string
  gerarLancamentoEntrada: boolean
  taxaJuros: string
  tipoTaxa: 'mensal' | 'anual'
  sistemaAmortizacao: SistemaAmortizacao
  indiceCorrecao: IndiceCorrecaoMonetaria
  periodicidade: TipoRecorrencia
  dataPrimeiraParcela: string
  quantidadeParcelas: string
  dataFinal: string
  anexoUrl: string
}

function emptyForm(): FormState {
  return {
    accountId: '', categoriaId: '', descricao: '', instituicao: '',
    valorTotal: '', valorEntrada: '', dataEntrada: '', gerarLancamentoEntrada: true,
    taxaJuros: '', tipoTaxa: 'mensal', sistemaAmortizacao: SistemaAmortizacao.Manual,
    indiceCorrecao: IndiceCorrecaoMonetaria.Nenhum, periodicidade: TipoRecorrencia.Mensal,
    dataPrimeiraParcela: new Date().toISOString().slice(0, 10), quantidadeParcelas: '12', dataFinal: '',
    anexoUrl: '',
  }
}

function dadosSimulacao(form: FormState) {
  const taxa = form.taxaJuros ? Number(form.taxaJuros) / 100 : undefined
  return {
    accountId: form.accountId,
    categoriaId: form.categoriaId || undefined,
    descricao: form.descricao,
    instituicao: form.instituicao || undefined,
    valorTotal: Number(form.valorTotal),
    valorEntrada: form.valorEntrada ? Number(form.valorEntrada) : undefined,
    taxaJurosMensal: form.tipoTaxa === 'mensal' ? taxa : undefined,
    taxaJurosAnual: form.tipoTaxa === 'anual' ? taxa : undefined,
    sistemaAmortizacao: form.sistemaAmortizacao,
    indiceCorrecao: form.indiceCorrecao,
    periodicidade: form.periodicidade,
    dataPrimeiraParcela: form.dataPrimeiraParcela,
    quantidadeParcelas: form.quantidadeParcelas ? Number(form.quantidadeParcelas) : null,
    dataFinal: form.quantidadeParcelas ? null : (form.dataFinal || null),
  }
}

export function FinanciamentosPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [erro, setErro] = useState<string | null>(null)
  const [projecao, setProjecao] = useState<SimulacaoFinanciamento | null>(null)
  const [paraExcluir, setParaExcluir] = useState<FinanciamentoResumo | null>(null)
  const [paraEncerrar, setParaEncerrar] = useState<FinanciamentoResumo | null>(null)

  const { data: page, isLoading, isError } = useQuery({
    queryKey: ['financiamentos'],
    queryFn: () => financiamentosApi.list(),
  })
  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => categoriasApi.list({ incluirInativas: false }),
    enabled: modalOpen,
  })
  const { data: contas = [] } = useQuery({ queryKey: ['contas'], queryFn: () => contasApi.list(), enabled: modalOpen })

  const simular = useMutation({
    mutationFn: () => financiamentosApi.simular(dadosSimulacao(form)),
    onSuccess: (r) => { setProjecao(r); setErro(null) },
    onError: (e: Error) => { setErro(e.message); setProjecao(null) },
  })

  const criar = useMutation({
    mutationFn: () => financiamentosApi.criar({
      ...dadosSimulacao(form),
      dataEntrada: form.dataEntrada || undefined,
      gerarLancamentoEntrada: form.gerarLancamentoEntrada,
      anexoUrl: form.anexoUrl || undefined,
    }),
    onSuccess: (f) => {
      qc.invalidateQueries({ queryKey: ['financiamentos'] })
      qc.invalidateQueries({ queryKey: ['transacoes'] })
      toast.success('Financiamento criado.')
      closeModal()
      navigate(`/financiamentos/${f.id}`)
    },
    onError: (e: Error) => setErro(e.message),
  })

  const encerrar = useMutation({
    mutationFn: (f: FinanciamentoResumo) => financiamentosApi.encerrar(f.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['financiamentos'] })
      toast.success('Financiamento encerrado.')
      setParaEncerrar(null)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const remover = useMutation({
    mutationFn: ({ id, apagar }: { id: string; apagar: boolean }) => financiamentosApi.remover(id, apagar),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['financiamentos'] })
      toast.success('Financiamento excluído.')
      setParaExcluir(null)
    },
    onError: (e: Error) => { toast.error(e.message); setParaExcluir(null) },
  })

  function openNew() {
    setForm(emptyForm())
    setErro(null)
    setProjecao(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setErro(null)
    setProjecao(null)
  }

  function submit() {
    if (!form.accountId || !form.descricao.trim() || !form.valorTotal) {
      setErro('Preencha conta, descrição e valor total.')
      return
    }
    if (!!form.quantidadeParcelas === !!form.dataFinal) {
      setErro('Informe a quantidade de parcelas ou a data final, não as duas nem nenhuma.')
      return
    }
    if (form.sistemaAmortizacao !== SistemaAmortizacao.Manual && !form.taxaJuros) {
      setErro('Informe a taxa de juros (mensal ou anual) para Price ou SAC.')
      return
    }
    criar.mutate()
  }

  const itens = page?.itens ?? []

  return (
    <>
      <PageHeader title="Financiamentos" />

      <Card>
        <CardHeader
          title="Meus financiamentos"
          actions={<Button icon="ri-add-line" onClick={openNew}>Novo financiamento</Button>}
        />
        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message="Erro ao carregar financiamentos." />
        ) : itens.length === 0 ? (
          <EmptyState icon="ri-bank-line" message="Nenhum financiamento cadastrado." action={<Button variant="soft-primary" icon="ri-add-line" onClick={openNew}>Novo financiamento</Button>} />
        ) : (
          <Table>
            <THead>
              <tr><Th>Descrição</Th><Th>Progresso</Th><Th>Próxima parcela</Th><Th>Status</Th><Th className="text-right">Valor total</Th><Th className="text-right">Ações</Th></tr>
            </THead>
            <tbody>
              {itens.map((f) => (
                <Tr key={f.id} onClick={() => navigate(`/financiamentos/${f.id}`)}>
                  <Td>
                    <div className="flex items-center gap-3">
                      <AvatarIcon icon="ri-bank-line" tone="primary" size="sm" />
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium text-white">
                          <span className="truncate">{f.descricao}</span>
                          {f.emAtraso && <Badge variant="expense">Atrasado</Badge>}
                        </p>
                        <p className="truncate text-2xs text-muted">{f.instituicao ?? '—'}</p>
                      </div>
                    </div>
                  </Td>
                  <Td className="min-w-[10rem]">
                    <p className="mb-1 text-2xs text-muted">{f.parcelasPagas} de {f.parcelasTotal} parcelas</p>
                    <ProgressBar value={f.parcelasTotal > 0 ? (f.parcelasPagas / f.parcelasTotal) * 100 : 0} />
                  </Td>
                  <Td className="whitespace-nowrap text-white">{f.proximaParcelaData ? formatDate(f.proximaParcelaData) : '—'}</Td>
                  <Td><Badge variant={f.status === StatusFinanciamento.Ativo ? 'income' : 'muted'}>{STATUS_FINANCIAMENTO_LABEL[f.status]}</Badge></Td>
                  <Td className="whitespace-nowrap text-right font-semibold text-white">{formatMoney(f.valorTotal)}</Td>
                  <Td className="whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                    <IconButton icon="ri-eye-line" label={`Ver ${f.descricao}`} tone="info" onClick={() => navigate(`/financiamentos/${f.id}`)} />
                    {f.status === StatusFinanciamento.Ativo && (
                      <IconButton icon="ri-stop-circle-line" label={`Encerrar ${f.descricao}`} tone="muted" onClick={() => setParaEncerrar(f)} />
                    )}
                    {f.parcelasPagas === 0 && (
                      <IconButton icon="ri-delete-bin-line" label={`Excluir ${f.descricao}`} tone="danger" onClick={() => setParaExcluir(f)} />
                    )}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Novo financiamento"
        size="lg"
        footer={
          <>
            <Button variant="light" onClick={closeModal}>Cancelar</Button>
            <Button icon="ri-add-line" onClick={submit} loading={criar.isPending}>Criar financiamento</Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Descrição" value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} placeholder="Ex: Financiamento do carro" />
            <Input label="Instituição" value={form.instituicao} onChange={(e) => setForm((f) => ({ ...f, instituicao: e.target.value }))} placeholder="Ex: Itaú" />
            <Select label="Conta" value={form.accountId} onChange={(e) => setForm((f) => ({ ...f, accountId: e.target.value }))}>
              <option value="">Selecione...</option>
              {contas.map((c) => <option key={c.id} value={c.id}>{c.name} · {c.institution}</option>)}
            </Select>
            <Select label="Categoria" value={form.categoriaId} onChange={(e) => setForm((f) => ({ ...f, categoriaId: e.target.value }))}>
              <option value="">Sem categoria</option>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.icone} {c.nome}</option>)}
            </Select>
            <Input label="Valor total (R$)" type="number" min={0} step="0.01" value={form.valorTotal} onChange={(e) => setForm((f) => ({ ...f, valorTotal: e.target.value }))} placeholder="0,00" />
            <div />
            <Input label="Valor da entrada (R$, opcional)" type="number" min={0} step="0.01" value={form.valorEntrada} onChange={(e) => setForm((f) => ({ ...f, valorEntrada: e.target.value }))} placeholder="0,00" />
            <Input label="Data da entrada" type="date" value={form.dataEntrada} onChange={(e) => setForm((f) => ({ ...f, dataEntrada: e.target.value }))} disabled={!form.valorEntrada} />
            {!!form.valorEntrada && (
              <label className="flex items-start gap-2 rounded border border-border/70 px-3 py-2.5 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={form.gerarLancamentoEntrada}
                  onChange={(e) => setForm((f) => ({ ...f, gerarLancamentoEntrada: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 accent-primary"
                />
                <span>
                  <span className="block text-13 text-white">Gerar lançamento da entrada</span>
                  <span className="block text-2xs text-muted">Desmarque se a entrada já foi paga fora do app (financiamento antigo).</span>
                </span>
              </label>
            )}
            <Select label="Sistema de amortização" value={form.sistemaAmortizacao} onChange={(e) => setForm((f) => ({ ...f, sistemaAmortizacao: Number(e.target.value) as SistemaAmortizacao }))}>
              {Object.entries(SISTEMA_AMORTIZACAO_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
            </Select>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Input
                  label="Taxa de juros (%)"
                  type="number" min={0} step="0.01"
                  value={form.taxaJuros}
                  disabled={form.sistemaAmortizacao === SistemaAmortizacao.Manual}
                  onChange={(e) => setForm((f) => ({ ...f, taxaJuros: e.target.value }))}
                  placeholder={form.tipoTaxa === 'mensal' ? 'Ex: 0,76' : 'Ex: 9,5'}
                />
              </div>
              <Select
                label="&nbsp;" value={form.tipoTaxa} disabled={form.sistemaAmortizacao === SistemaAmortizacao.Manual}
                onChange={(e) => setForm((f) => ({ ...f, tipoTaxa: e.target.value as 'mensal' | 'anual' }))}
                className="w-28"
              >
                <option value="mensal">ao mês</option>
                <option value="anual">ao ano</option>
              </Select>
            </div>
            {form.sistemaAmortizacao !== SistemaAmortizacao.Manual && (
              <Select
                label="Correção monetária (opcional)" value={form.indiceCorrecao}
                onChange={(e) => setForm((f) => ({ ...f, indiceCorrecao: Number(e.target.value) as IndiceCorrecaoMonetaria }))}
              >
                {Object.entries(INDICE_CORRECAO_LABEL).map(([valor, label]) => <option key={valor} value={valor}>{label}</option>)}
              </Select>
            )}
            <Select label="Periodicidade" value={form.periodicidade} onChange={(e) => setForm((f) => ({ ...f, periodicidade: Number(e.target.value) as TipoRecorrencia }))}>
              {PERIODICIDADES.map((p) => <option key={p} value={p}>{TIPO_RECORRENCIA_LABEL[p]}</option>)}
            </Select>
            <Input label="Data da primeira parcela" type="date" value={form.dataPrimeiraParcela} onChange={(e) => setForm((f) => ({ ...f, dataPrimeiraParcela: e.target.value }))} />
            <Input
              label="Quantidade de parcelas"
              type="number" min={1} max={600} step={1}
              value={form.quantidadeParcelas}
              onChange={(e) => setForm((f) => ({ ...f, quantidadeParcelas: e.target.value, dataFinal: e.target.value ? '' : f.dataFinal }))}
              placeholder="Ex: 48"
            />
            <Input
              label="Data final"
              type="date"
              min={form.dataPrimeiraParcela}
              value={form.dataFinal}
              onChange={(e) => setForm((f) => ({ ...f, dataFinal: e.target.value, quantidadeParcelas: e.target.value ? '' : f.quantidadeParcelas }))}
            />
            <p className="self-center text-2xs text-muted sm:col-span-2">Preencha a quantidade de parcelas ou a data final, não as duas.</p>
            <Input label="URL do contrato (PDF, opcional)" icon="ri-attachment-2" value={form.anexoUrl} onChange={(e) => setForm((f) => ({ ...f, anexoUrl: e.target.value }))} placeholder="https://..." className="sm:col-span-2" />
          </div>

          <div className="flex items-center justify-between rounded border border-border/70 px-3 py-2.5">
            <p className="text-13 text-muted">Confira a projeção das parcelas antes de criar.</p>
            <Button variant="soft-info" size="sm" icon="ri-calculator-line" loading={simular.isPending} onClick={() => simular.mutate()}>Ver projeção</Button>
          </div>

          {projecao && (
            <div className="max-h-56 overflow-y-auto rounded border border-border/70">
              <Table>
                <THead><tr><Th>#</Th><Th>Data</Th><Th className="text-right">Valor</Th><Th className="text-right">Juros</Th></tr></THead>
                <tbody>
                  {projecao.parcelas.map((p) => (
                    <Tr key={p.numero}>
                      <Td>{p.numero}</Td>
                      <Td className="whitespace-nowrap">{formatDate(p.data)}</Td>
                      <Td className="text-right">{formatMoney(p.valor)}</Td>
                      <Td className="text-right text-muted">{formatMoney(p.juros)}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          {erro && <p className="flex items-center gap-1.5 rounded bg-expense/10 px-3 py-2 text-13 text-expense" role="alert"><i className="ri-error-warning-line" aria-hidden /> {erro}</p>}
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraEncerrar}
        onClose={() => setParaEncerrar(null)}
        onConfirm={() => paraEncerrar && encerrar.mutate(paraEncerrar)}
        loading={encerrar.isPending}
        title="Encerrar financiamento?"
        confirmLabel="Sim, encerrar"
        message={<>As parcelas em aberto de <strong className="text-white">{paraEncerrar?.descricao}</strong> serão canceladas. O histórico já pago é mantido.</>}
      />

      {/* com parcelas pagas o usuário escolhe o destino dos lançamentos já executados; as futuras saem sempre */}
      <Modal
        open={!!paraExcluir && paraExcluir.parcelasPagas > 0}
        onClose={() => setParaExcluir(null)}
        title="Excluir financiamento?"
        size="sm"
        footer={<Button variant="light" onClick={() => setParaExcluir(null)}>Cancelar</Button>}
      >
        <div className="space-y-3">
          <p className="text-13 text-muted">
            <strong className="text-white">{paraExcluir?.descricao}</strong> tem {paraExcluir?.parcelasPagas} parcela(s) paga(s). As parcelas futuras (em aberto) são excluídas junto com o financiamento. O que fazer com os lançamentos já pagos?
          </p>
          <Button variant="danger" className="w-full" loading={remover.isPending} onClick={() => paraExcluir && remover.mutate({ id: paraExcluir.id, apagar: true })}>
            Apagar também os lançamentos pagos
          </Button>
          <p className="text-2xs text-muted">Devolve o valor às contas, como se nunca tivessem sido pagos.</p>
          <Button variant="light" className="w-full" loading={remover.isPending} onClick={() => paraExcluir && remover.mutate({ id: paraExcluir.id, apagar: false })}>
            Manter os lançamentos pagos
          </Button>
          <p className="text-2xs text-muted">Continuam no histórico e no saldo como despesas comuns.</p>
        </div>
      </Modal>

      <ConfirmDialog
        open={!!paraExcluir && paraExcluir.parcelasPagas === 0}
        onClose={() => setParaExcluir(null)}
        onConfirm={() => paraExcluir && remover.mutate({ id: paraExcluir.id, apagar: false })}
        loading={remover.isPending}
        title="Excluir financiamento?"
        confirmLabel="Sim, excluir"
        message={<>O financiamento <strong className="text-white">{paraExcluir?.descricao}</strong> e todas as suas parcelas serão excluídos.</>}
      />
    </>
  )
}
